/* ==========================================================================
   Progressive product loading for the collection grid
   (sections/main-collection-product-grid.liquid)

   One controller, two page sources. The section decides where a batch of
   cards comes from — a Section Rendering fetch of the next `{% paginate %}`
   page, or the next slice of an in-memory list (Featured sort / in-collection
   search) — and hands the controller a `fetchPage(page, signal)` function.
   Everything that is easy to get wrong is here, once, for both sources:

     - only ever one request in flight
     - a generation token, so a batch that resolves after the customer
       switched collection / sort / search is discarded, never appended
     - AbortController on every reset and teardown
     - a rendered-id set, so the same product can never appear twice
     - the observer stops for good once the last page is in
     - a chain guard, so a short batch can't cascade into "load everything"

   The controller owns no markup of its own: the section passes in the
   elements and the already-translated strings. It never reads a collection
   handle, a product, or a URL.

   Page 1 is always the server-rendered grid. This is an enhancement layered
   on top of Shopify's own pagination, which stays in the HTML for crawlers
   and for customers without JavaScript — see docs/COLLECTION_INFINITE_SCROLL.md.
   ========================================================================== */
(function () {
  'use strict';

  /* Start fetching roughly one viewport before the customer reaches the end,
     so a batch is usually already in place by the time they get there. */
  var ROOT_MARGIN = '800px 0px';

  /* An append that does not push the sentinel out of view triggers one more
     load, which is what makes a short batch (or a very tall screen) still
     reach the end. Capped so a pathological case degrades into the Load more
     button instead of draining every page in a loop. */
  var MAX_CHAIN = 3;

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function create(options) {
    var grid = options.grid;
    var els = options.elements || {};
    var strings = options.strings || {};
    var mode = options.mode === 'load_more' ? 'load_more' : 'infinite';

    if (!grid) return null;

    var observer = null;
    var controller = null;
    var generation = 0;
    var loading = false;
    var chain = 0;
    var sentinelVisible = false;
    var scrolledSinceLoad = true;

    var source = null;      // { fetchPage, nextPage, hasMore }
    var renderedIds = null; // Set-like map of product ids already in the grid

    /* ---- rendered-id bookkeeping ----
       Cards carry data-product-id from both card builders (the Liquid snippet
       and the section's client-side builder), so one dedupe rule covers every
       way a card can reach the grid. A card without an id is still appended —
       dedupe is a safety net, not a gate. */
    function indexGrid() {
      renderedIds = {};
      var cards = grid.querySelectorAll('[data-product-id]');
      for (var i = 0; i < cards.length; i++) {
        renderedIds[cards[i].getAttribute('data-product-id')] = true;
      }
    }

    function isNew(node) {
      if (!node || node.nodeType !== 1) return false;
      var id = node.getAttribute && node.getAttribute('data-product-id');
      if (!id) return true;
      if (renderedIds[id]) return false;
      renderedIds[id] = true;
      return true;
    }

    function renderedCount() {
      return grid.querySelectorAll('.bs-product-card').length;
    }

    /* ---- status ---- */
    function announce(text) {
      if (els.status) els.status.textContent = text || '';
    }

    function setBusy(isBusy) {
      if (els.skeleton) els.skeleton.hidden = !isBusy;
      if (els.root) els.root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    function paint() {
      if (!els.root) return;
      var done = !source || !source.hasMore;
      els.root.hidden = false;
      hide(els.error);
      if (done) {
        // Last page is in: release the observer for good rather than leave it
        // watching a sentinel nothing will ever act on again.
        teardownObserver();
        hide(els.button);
        setBusy(false);
        // The end note is only worth showing once something was actually
        // appended — on a single-page collection it would be noise.
        if (els.end) els.end.hidden = !source || source.nextPage <= 2;
        return;
      }
      hide(els.end);
      if (mode === 'load_more' && !loading) show(els.button); else hide(els.button);
    }

    function fail() {
      loading = false;
      setBusy(false);
      hide(els.end);
      show(els.error);
      show(els.button); // the retry path is the same button, in every mode
      announce(strings.error || '');
    }

    /* ---- loading ---- */
    function loadNext(fromUser) {
      if (loading || !source || !source.hasMore) return;
      if (!grid.isConnected) { teardownObserver(); return; }

      if (fromUser) { chain = 0; scrolledSinceLoad = true; }
      loading = true;
      chain++;
      hide(els.error);
      hide(els.button);
      setBusy(true);
      announce(strings.loading || '');

      var myGeneration = generation;
      var page = source.nextPage;

      if (controller) controller.abort();
      controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      source.fetchPage(page, controller ? controller.signal : null).then(function (result) {
        // Anything that resets the controller (collection switch, sort,
        // search) bumps the generation; a batch from before that point
        // belongs to a result set the customer is no longer looking at.
        if (myGeneration !== generation) return;
        if (!grid.isConnected) { teardownObserver(); return; }

        var nodes = (result && result.nodes) || [];
        var added = 0;
        for (var i = 0; i < nodes.length; i++) {
          if (isNew(nodes[i])) { grid.appendChild(nodes[i]); added++; }
        }

        source.nextPage = page + 1;
        // A page that adds nothing new ends progressive loading rather than
        // advancing again: the only way that happens is a source repeating
        // itself, and retrying it is how a request loop starts.
        source.hasMore = !!(result && result.hasMore) && added > 0;

        loading = false;
        setBusy(false);
        if (added && typeof options.onAppend === 'function') options.onAppend(grid);

        var total = (result && result.total) || 0;
        if (strings.showing && total) {
          announce(strings.showing.replace('__COUNT__', renderedCount()).replace('__TOTAL__', total));
        } else if (!source.hasMore) {
          announce(strings.end || '');
        }

        paint();
        maybeContinue();
      }).catch(function (err) {
        if (myGeneration !== generation) return;
        if (err && err.name === 'AbortError') { loading = false; return; }
        fail();
      });
    }

    /* After an append, the sentinel can still be on screen — a short batch,
       a tall window, or a two-column phone layout. Re-checking its real
       position (rather than waiting for another observer callback, which
       only fires on a state change) is what lets loading continue in those
       cases, while the chain cap keeps it from running away. */
    function maybeContinue() {
      if (mode !== 'infinite' || !source || !source.hasMore) return;
      if (chain >= MAX_CHAIN && !scrolledSinceLoad) { show(els.button); return; }
      if (!els.sentinel) return;
      requestAnimationFrame(function () {
        if (!grid.isConnected || loading) return;
        var box = els.sentinel.getBoundingClientRect();
        if (box.top <= (window.innerHeight || 0) + 200) {
          scrolledSinceLoad = false;
          loadNext(false);
        }
      });
    }

    /* ---- observer ---- */
    function teardownObserver() {
      if (observer) { observer.disconnect(); observer = null; }
    }

    function setupObserver() {
      teardownObserver();
      if (mode !== 'infinite' || !els.sentinel || typeof IntersectionObserver === 'undefined') return;
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          sentinelVisible = entries[i].isIntersecting;
          if (sentinelVisible) { scrolledSinceLoad = true; chain = 0; loadNext(false); }
        }
      }, { rootMargin: ROOT_MARGIN });
      observer.observe(els.sentinel);
    }

    function onScroll() { scrolledSinceLoad = true; }

    /* ---- public ----
       start() is also the reset: every call abandons whatever was in flight,
       re-reads the grid, and begins a brand-new result set. The section calls
       it on first render and again on every sort/search/restore. */
    function start(newSource) {
      generation++;
      loading = false;
      chain = 0;
      if (controller) { controller.abort(); controller = null; }
      source = newSource || null;
      indexGrid();
      setBusy(false);
      hide(els.error);
      hide(els.end);
      paint();
      if (!source || !source.hasMore) { teardownObserver(); return; }
      setupObserver();
      // A grid shorter than the viewport would otherwise never fire the
      // observer at all on a large screen.
      maybeContinue();
    }

    function stop() {
      generation++;
      loading = false;
      if (controller) { controller.abort(); controller = null; }
      source = null;
      teardownObserver();
      if (els.root) els.root.hidden = true;
    }

    function destroy() {
      stop();
      window.removeEventListener('scroll', onScroll);
      if (els.button) els.button.removeEventListener('click', onButton);
    }

    function onButton() { loadNext(true); }

    if (els.button) els.button.addEventListener('click', onButton);
    window.addEventListener('scroll', onScroll, { passive: true });

    return {
      start: start,
      stop: stop,
      destroy: destroy,
      loadNext: function () { loadNext(true); },
      isActive: function () { return !!(source && source.hasMore); }
    };
  }

  window.BSProgressiveGrid = { create: create };
})();
